import type { HTMLAttributes, TdHTMLAttributes, ThHTMLAttributes } from 'react';

import { cn } from '../lib/cn.js';

/**
 * Data table primitives.
 *
 * The wrapper owns the horizontal scroll so a wide table scrolls inside its
 * card instead of forcing the whole page sideways on a narrow screen.
 */
export const TableWrap = ({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>): React.JSX.Element => (
  <div
    className={cn('w-full overflow-x-auto rounded-lg border bg-card shadow-sm', className)}
    {...props}
  />
);

export const Table = ({
  className,
  ...props
}: HTMLAttributes<HTMLTableElement>): React.JSX.Element => (
  <table className={cn('w-full caption-bottom text-sm', className)} {...props} />
);

export const THead = ({
  className,
  ...props
}: HTMLAttributes<HTMLTableSectionElement>): React.JSX.Element => (
  <thead className={cn('border-b bg-muted/40', className)} {...props} />
);

export const TBody = ({
  className,
  ...props
}: HTMLAttributes<HTMLTableSectionElement>): React.JSX.Element => (
  <tbody className={cn('[&_tr:last-child]:border-0', className)} {...props} />
);

export const TR = ({
  className,
  ...props
}: HTMLAttributes<HTMLTableRowElement>): React.JSX.Element => (
  <tr className={cn('border-b transition-colors hover:bg-muted/40', className)} {...props} />
);

export const TH = ({
  className,
  ...props
}: ThHTMLAttributes<HTMLTableCellElement>): React.JSX.Element => (
  <th
    className={cn(
      'h-10 px-4 text-left align-middle text-xs font-medium uppercase tracking-wide text-muted-foreground',
      className,
    )}
    {...props}
  />
);

export const TD = ({
  className,
  ...props
}: TdHTMLAttributes<HTMLTableCellElement>): React.JSX.Element => (
  <td className={cn('px-4 py-3 align-middle', className)} {...props} />
);
